import { useState } from "react";
import { Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { sendContact } from "@/lib/api";
import { CONTACT_EMAIL } from "@/constants/socials";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.includes("@") || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await sendContact(form);
      toast.success("Message sent. We'll get back to you shortly.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Could not send your message. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 sm:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#0052FF] mb-5">
              Get In Touch
            </div>
            <h2
              data-testid="contact-headline"
              className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1] text-[#0A0A0A]"
            >
              Contact.
            </h2>
            <p className="mt-5 text-lg text-[#525252] max-w-md">
              Questions, partnerships, sponsorships, or affiliate inquiries —
              drop us a line.
            </p>

            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="contact-email-link"
              className="mt-8 inline-flex items-center gap-3 rounded-xl border border-[#E5E5E5] bg-[#FAFAFA] px-4 py-3 hover:border-[#0A0A0A]/20 transition-colors"
            >
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-white border border-[#E5E5E5] text-[#0052FF]">
                <Mail size={18} />
              </span>
              <div className="flex flex-col text-left">
                <span className="text-xs uppercase tracking-wider font-semibold text-[#525252]">
                  Email
                </span>
                <span className="text-sm font-semibold text-[#0A0A0A]">
                  {CONTACT_EMAIL}
                </span>
              </div>
            </a>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 bg-[#FAFAFA] border border-[#E5E5E5] rounded-2xl p-6 sm:p-10 space-y-5"
          >
            <div>
              <label
                htmlFor="contact-name"
                className="block text-sm font-semibold text-[#0A0A0A] mb-2"
              >
                Name
              </label>
              <input
                id="contact-name"
                type="text"
                value={form.name}
                onChange={onChange("name")}
                placeholder="Your full name"
                data-testid="contact-name-input"
                required
                className="w-full bg-white border border-[#E5E5E5] rounded-lg px-4 py-3 text-[#0A0A0A] placeholder:text-[#9CA3AF] outline-none focus:border-[#0052FF] focus:ring-2 focus:ring-[#0052FF]/20 transition-all"
              />
            </div>

            <div>
              <label
                htmlFor="contact-email"
                className="block text-sm font-semibold text-[#0A0A0A] mb-2"
              >
                Email
              </label>
              <input
                id="contact-email"
                type="email"
                value={form.email}
                onChange={onChange("email")}
                placeholder="you@company.com"
                data-testid="contact-email-input"
                required
                className="w-full bg-white border border-[#E5E5E5] rounded-lg px-4 py-3 text-[#0A0A0A] placeholder:text-[#9CA3AF] outline-none focus:border-[#0052FF] focus:ring-2 focus:ring-[#0052FF]/20 transition-all"
              />
            </div>

            <div>
              <label
                htmlFor="contact-message"
                className="block text-sm font-semibold text-[#0A0A0A] mb-2"
              >
                Message
              </label>
              <textarea
                id="contact-message"
                rows={5}
                value={form.message}
                onChange={onChange("message")}
                placeholder="Tell us what you're working on…"
                data-testid="contact-message-input"
                required
                className="w-full bg-white border border-[#E5E5E5] rounded-lg px-4 py-3 text-[#0A0A0A] placeholder:text-[#9CA3AF] outline-none focus:border-[#0052FF] focus:ring-2 focus:ring-[#0052FF]/20 transition-all resize-y"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-button"
              className="inline-flex items-center justify-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] disabled:opacity-70 text-white font-semibold py-3 px-6 rounded-lg transition-colors shadow-sm shadow-[#0052FF]/20 focus:outline-none focus:ring-2 focus:ring-[#0052FF] focus:ring-offset-2"
            >
              {loading ? "Sending…" : "Send Message"}
              {!loading && <Send size={16} />}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
